import { prepareSceneSimulation } from "../utils/prepareSceneSimulation";
import { orientationsFromConfig } from "../components/config/orientations";

/*
 * The layout's shading calculation, for every caller: the layout's own
 * "Calculate shading", the estimate's question and the study's notice.
 *
 * It runs in a Web Worker, so the page keeps painting, its progress is real and
 * stopping it is immediate. While it runs the whole application is locked
 * behind one modal (SceneCalculationOverlay): the calculation belongs to the
 * layout as it stands on the page, usually before that layout is saved, and
 * leaving the page would leave its result nowhere to go. The browser's back
 * button is held for the same reason, and closing or reloading the tab asks
 * first. Where workers are not available the same calculation runs on the
 * page, as it always did.
 */

const listeners = new Set();
let active = null;

export function subscribeSceneCalculation(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function sceneCalculationState() {
  return active;
}

function publish(next) {
  active = next;
  listeners.forEach((listener) => listener(active));
}

const CANCELLED = "Scene preparation cancelled.";

export function isSceneCalculationCancelled(error) {
  return Boolean(error?.cancelled) || error?.message === CANCELLED;
}

function cancelledError() {
  return Object.assign(new Error(CANCELLED), { cancelled: true });
}

// Back held in place and the tab's closing questioned, for as long as it runs.
function lockNavigation() {
  if (typeof window === "undefined") return () => {};
  const onBeforeUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
  window.addEventListener("beforeunload", onBeforeUnload);
  const mark = `scene-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let onPopState = null;
  try {
    // An entry of the same page on top: Back lands on the page itself and the
    // entry goes straight back on, so the route never changes underneath.
    const hold = () => window.history.pushState({ ...(window.history.state || {}), sceneCalculation: mark }, "", window.location.href);
    hold();
    onPopState = hold;
    window.addEventListener("popstate", onPopState);
  } catch { onPopState = null; }
  return () => {
    window.removeEventListener("beforeunload", onBeforeUnload);
    if (!onPopState) return;
    window.removeEventListener("popstate", onPopState);
    // Take the extra entry away again, so the next Back is a real one.
    try { if (window.history.state?.sceneCalculation === mark) window.history.back(); } catch { /* nothing to undo */ }
  };
}

async function inWorker(config, { module, projectId, onProgress, cancelled }) {
  const { createScenePreparationWorker } = await import("../workers/createScenePreparationWorker");
  let worker;
  try { worker = createScenePreparationWorker(); } catch { throw Object.assign(new Error("worker unavailable"), { workerFailed: true }); }
  return new Promise((resolve, reject) => {
    let poll = null;
    let settled = false;
    const finish = (settle, value) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      worker.terminate();
      settle(value);
    };
    poll = setInterval(() => { if (cancelled()) finish(reject, cancelledError()); }, 100);
    worker.onmessage = ({ data }) => {
      if (data?.type === "progress") { if (!cancelled()) onProgress(data.value); }
      else if (data?.type === "done") finish(resolve, data.config);
      else if (data?.type === "error") {
        finish(reject, Object.assign(new Error(data.error?.message || "Scene preparation failed."), data.error || {}));
      }
    };
    // The worker could not start (its script did not load): the page does it.
    worker.onerror = (event) => {
      event?.preventDefault?.();
      finish(reject, Object.assign(new Error("worker unavailable"), { workerFailed: true }));
    };
    worker.postMessage({ config, module, projectId });
  });
}

/**
 * prepareSceneSimulation, off the page's thread and behind the application's
 * lock. Same arguments, same result; a stop -- the overlay's button or the
 * caller's shouldCancel -- rejects with an error isSceneCalculationCancelled
 * recognises.
 */
export async function prepareSceneInBackground(config, { module, projectId, onProgress = () => {}, shouldCancel = () => false } = {}) {
  const token = {};
  let stopped = false;
  const cancelled = () => stopped || Boolean(shouldCancel());
  const faces = Math.max(1, orientationsFromConfig(config).length);
  publish({ token, progress: 0, faces, cancel: () => { stopped = true; } });
  const unlock = lockNavigation();
  const progress = (value) => {
    onProgress(value);
    if (active?.token === token) publish({ ...active, progress: value });
  };
  try {
    if (typeof Worker !== "undefined") {
      try {
        return await inWorker(config, { module, projectId, onProgress: progress, cancelled });
      } catch (error) {
        if (!error?.workerFailed) throw error;
      }
    }
    try {
      return await prepareSceneSimulation(config, { module, projectId, onProgress: progress, shouldCancel: cancelled });
    } catch (error) {
      throw cancelled() ? cancelledError() : error;
    }
  } finally {
    unlock();
    if (active?.token === token) publish(null);
  }
}
